'use client';

import type { ReactNode } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { InternalNetworkCredentialDto } from '@terra/shared-contracts-internal-network';
import { Alert } from '../../../components/alert';
import { Skeleton } from '../../../components/skeleton';
import { ConfirmationDialog } from '../../confirmation-dialog/ConfirmationDialog';
import { SettingsEmptyState } from '../../settings/SettingsEmptyState';
import { AddInternalNetworkCredentialDialog } from './AddInternalNetworkCredentialDialog';
import { InternalNetworkCredentialCard } from './InternalNetworkCredentialCard';
import { InternalNetworkCredentialsAddButton } from './InternalNetworkCredentialsAddButton';
import { canVerifyInternalNetworkCredential } from './internal-credentials.model';
import type { InternalNetworkCredentialVerificationBlockReason } from './internal-credential-verification-readiness.model';
import { useInternalNetworkCredentialBlockReasonLabel } from './useInternalNetworkCredentialBlockReasonLabel';
import {
  useInternalNetworkCredentialsManager,
  type InternalNetworkCredentialsManagerCallbacks,
} from './useInternalNetworkCredentialsManager';

export type InternalNetworkCredentialsManagerContentProps =
  InternalNetworkCredentialsManagerCallbacks & {
    credentials: InternalNetworkCredentialDto[];
    isLoading: boolean;
    isSaving: boolean;
    canVerify: boolean;
    verificationBlockReason: InternalNetworkCredentialVerificationBlockReason | null;
    isReadOnly?: boolean;
    defaultDomain?: string;
    gateActionsUntilSensorReady?: boolean;
    hidePrerequisitesBanner?: boolean;
    headerContent?: ReactNode;
  };

export function InternalNetworkCredentialsManagerContent({
  credentials,
  isLoading,
  isSaving,
  canVerify,
  verificationBlockReason,
  isReadOnly,
  defaultDomain,
  gateActionsUntilSensorReady,
  hidePrerequisitesBanner,
  headerContent,
  onCreate,
  onUpdate,
  onDelete,
  onVerify,
  onSetOwned,
}: InternalNetworkCredentialsManagerContentProps) {
  const t = useTranslations('designSystem.networkSettings.internalCredentials');
  const tCredentials = useTranslations('designSystem.credentials');
  const resolveBlockReasonLabel =
    useInternalNetworkCredentialBlockReasonLabel();
  const verifyBlockReasonLabel = resolveBlockReasonLabel(
    verificationBlockReason,
  );
  const manager = useInternalNetworkCredentialsManager({
    credentials,
    canVerify,
    verifyBlockReasonLabel,
    onCreate,
    onUpdate,
    onDelete,
    onVerify,
    onSetOwned,
  });
  const isSensorGateActive = !!gateActionsUntilSensorReady && !canVerify;
  const sensorGatedActionsDisabled = !!isReadOnly || isSensorGateActive;
  const sensorGateTooltip = isSensorGateActive
    ? (verifyBlockReasonLabel ?? undefined)
    : undefined;
  const showPrerequisitesBanner =
    !hidePrerequisitesBanner && !canVerify && !!verifyBlockReasonLabel;
  const deletingCredentialName =
    manager.deletingCredential?.name ||
    manager.deletingCredential?.username ||
    '';

  return (
    <>
      <VStack alignItems="stretch" gap={4}>
        {headerContent}

        {showPrerequisitesBanner ? (
          <Alert.Root bg="bg.neutral.info" borderColor="border.neutral.info">
            <Alert.Content>
              <Alert.Description>{verifyBlockReasonLabel}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
        ) : (
          <Alert.Root bg="bg.neutral.info" borderColor="border.neutral.subtle">
            <Alert.Content>
              <Alert.Description>
                {t('ownedScopeDescription')}
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}

        {!isReadOnly ? (
          <HStack justifyContent="flex-end">
            <InternalNetworkCredentialsAddButton
              disabled={sensorGatedActionsDisabled}
              tooltip={sensorGateTooltip}
              onAdd={manager.openCreateDialog}
            />
          </HStack>
        ) : null}

        {isLoading ? (
          <Skeleton height="55px" borderRadius="md" />
        ) : credentials.length > 0 ? (
          <VStack alignItems="stretch" gap={3}>
            {credentials.map((credential) => {
              const verification = manager.resolveVerificationState(credential);

              return (
                <InternalNetworkCredentialCard
                  key={credential.id}
                  credential={credential}
                  verificationStatus={verification.status}
                  verificationError={verification.lastError}
                  isTesting={manager.testingCredentialIds.has(credential.id)}
                  isTestDisabled={
                    !canVerifyInternalNetworkCredential({
                      canVerify,
                      credentialType: credential.credentialType,
                    })
                  }
                  isOwnedTogglePending={manager.ownedToggleCredentialIds.has(
                    credential.id,
                  )}
                  isReadOnly={isReadOnly}
                  onEdit={manager.openEditDialog}
                  onDelete={manager.setDeletingCredential}
                  onTest={(credentialId) =>
                    void manager.testCredential({
                      credentialId,
                      credentialType: credential.credentialType,
                    })
                  }
                  onToggleOwned={(toggle) =>
                    void manager.handleToggleOwned(toggle)
                  }
                />
              );
            })}
          </VStack>
        ) : (
          <SettingsEmptyState>{t('emptyStateDescription')}</SettingsEmptyState>
        )}
      </VStack>

      <AddInternalNetworkCredentialDialog
        open={manager.isDialogOpen}
        onOpenChange={manager.handleDialogOpenChange}
        initialCredential={manager.editingCredential}
        defaultDomain={defaultDomain}
        defaultCredentialType={manager.createCredentialType}
        loading={isSaving}
        onSave={manager.handleSave}
      />

      <ConfirmationDialog
        isOpen={manager.deletingCredential !== null}
        loading={manager.isDeleting}
        onOpenChange={({ open }) => {
          if (!open) {
            manager.setDeletingCredential(null);
          }
        }}
        onConfirm={() => void manager.handleConfirmDelete()}
        onCancel={() => manager.setDeletingCredential(null)}
        variant="destructive"
        title={tCredentials('deleteConfirm.title')}
        description={tCredentials('deleteConfirm.description', {
          name: deletingCredentialName,
        })}
        confirmText={tCredentials('deleteConfirm.confirm')}
        cancelText={tCredentials('deleteConfirm.cancel')}
        confirmAutomationId="internal-network-credential-delete-confirm"
      />
    </>
  );
}
